import {API_BASE_URL} from "../config/api";
import {RoundTripFlight} from "../models/RoundTripFlight";

export interface SharedShortlist {
  id: string;
  flights: {id: string; flight: RoundTripFlight; votes: number}[];
  total_votes: number;
  my_vote: string | null;
  leaders: string[];
  can_manage: boolean;
  created_at: number;
  expires_at: number;
  revision: number;
}

export class ShortlistError extends Error {
  constructor(public status: number) { super("Shortlist request failed"); }
}

// Separate from the public URL. Keeping this token is what lets this browser
// manage its list and replace its vote on later visits without an account.
export function browserToken(): string {
  const key = "farewise.shortlist.identity";
  let token = localStorage.getItem(key);
  if (!token || !/^[A-Za-z0-9_-]{43}$/.test(token)) {
    const bytes = window.crypto.getRandomValues(new Uint8Array(32));
    token = btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(""))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    localStorage.setItem(key, token);
  }
  return token;
}

async function request<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  // Public viewing still works when browser storage is disabled.
  let token: string | undefined;
  try { token = browserToken(); } catch (error) { if (method !== "GET") throw error; }
  const response = await fetch(`${API_BASE_URL}/shortlists${path}`, {
    method, cache: "no-store",
    headers: {...(body ? {"Content-Type": "application/json"} : {}),
      ...(token ? {Authorization: `Bearer ${token}`} : {})},
    ...(body ? {body: JSON.stringify(body)} : {}),
  });
  if (!response.ok) throw new ShortlistError(response.status);
  return response.status === 204 ? undefined as T : response.json();
}

export const ShortlistService = {
  get: (id: string) => request<SharedShortlist>(`/${encodeURIComponent(id)}`),
  create: (flights: RoundTripFlight[]) => request<SharedShortlist>("", "POST", {flights}),
  update: (id: string, flights: RoundTripFlight[]) =>
    request<SharedShortlist>(`/${encodeURIComponent(id)}/flights`, "POST", {flights}),
  vote: (id: string, flightId: string) =>
    request<SharedShortlist>(`/${encodeURIComponent(id)}/votes`, "POST", {flight_id: flightId}),
  delete: (id: string) => request<void>(`/${encodeURIComponent(id)}`, "DELETE"),
  url: (id: string) => `${window.location.origin}/shortlists/${encodeURIComponent(id)}`,
};
